import React, { useState } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const FormInput = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  error,
  success,
  helpText,
  required = false,
  disabled = false,
  placeholder,
  className = '',
  autoComplete,
  maxLength,
  pattern,
  showPasswordToggle = false,
  validateOnChange = true,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [localError, setLocalError] = useState('');

  const hasError = error || localError;
  const showSuccess = success && !hasError && isTouched && value;

  const handleFocus = (e) => {
    // For number inputs, select all text on focus for easy replacement
    if (type === 'number' || type === 'tel') {
      e.target.select();
    }
  };

  const handleBlur = (e) => {
    setIsTouched(true);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }

    // Real-time validation
    if (validateOnChange && isTouched) {
      const { value } = e.target;
      
      if (required && !value) {
        setLocalError('שדה חובה');
      } else if (type === 'email' && value && !/\S+@\S+\.\S+/.test(value)) {
        setLocalError('כתובת אימייל לא תקינה');
      } else if (pattern && value && !new RegExp(pattern).test(value)) {
        setLocalError('פורמט לא תקין');
      } else if (maxLength && value.length > maxLength) {
        setLocalError(`מקסימום ${maxLength} תווים`);
      } else {
        setLocalError('');
      }
    }
  };

  const inputType = showPasswordToggle && showPassword ? 'text' : type;

  const inputClasses = `
    block w-full px-4 py-2.5 sm:py-2 text-sm sm:text-base
    border rounded-lg 
    transition-all duration-200
    disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
    focus:outline-none focus:ring-2 focus:ring-offset-0
    ${hasError && isTouched 
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500 pr-10' 
      : showSuccess 
        ? 'border-green-300 focus:border-green-500 focus:ring-green-500 pr-10'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
    }
    ${showPasswordToggle ? 'pl-12' : ''}
    ${className}
  `.trim();

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={name} 
          className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5"
        >
          {label}
          {required && <span className="text-red-500 mr-1" aria-label="שדה חובה">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={name}
          name={name}
          type={inputType}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          maxLength={maxLength}
          pattern={pattern}
          className={inputClasses}
          aria-invalid={hasError && isTouched ? 'true' : 'false'}
          aria-describedby={
            hasError && isTouched ? `${name}-error` : 
            helpText ? `${name}-help` : undefined
          }
          {...props}
        />

        {/* Validation Icons */}
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          {hasError && isTouched && (
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          )}
          {showSuccess && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          )}
        </div>

        {/* Password Toggle */}
        {showPasswordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
            aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        )}
      </div>

      {/* Error Message */}
      {hasError && isTouched && (
        <p 
          id={`${name}-error`}
          className="mt-1.5 text-xs sm:text-sm text-red-600 flex items-center gap-1"
          role="alert"
        >
          <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{hasError}</span>
        </p>
      )}

      {/* Help Text */}
      {helpText && !hasError && (
        <p 
          id={`${name}-help`}
          className="mt-1.5 text-xs sm:text-sm text-gray-500"
        >
          {helpText}
        </p>
      )}

      {/* Character Count */}
      {maxLength && value && (
        <p
          className="mt-1 text-xs text-gray-500 text-left"
          aria-live="polite"
          aria-atomic="true"
        >
          {value.length}/{maxLength}
        </p>
      )}
    </div>
  );
};

export default FormInput;

